import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8999"

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
})

type RetriedRequest = InternalAxiosRequestConfig & { _retried?: boolean }

const REFRESH_NETWORK_RETRY_MAX = 2
const REFRESH_NETWORK_RETRY_BACKOFF_MS = 500

let refreshInFlight: Promise<void> | null = null

function isNetworkOrTransient(err: unknown): boolean {
  const ax = err as AxiosError | undefined
  if (!ax) return false
  if (!ax.response) return true
  const status = ax.response.status
  return status >= 500 && status < 600
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function postRefreshWithRetry(): Promise<void> {
  let attempt = 0
  let lastErr: unknown
  while (attempt <= REFRESH_NETWORK_RETRY_MAX) {
    try {
      await api.post("/api/auth/refresh")
      return
    } catch (err) {
      lastErr = err
      if (!isNetworkOrTransient(err) || attempt === REFRESH_NETWORK_RETRY_MAX) {
        throw err
      }
      await sleep(REFRESH_NETWORK_RETRY_BACKOFF_MS * (attempt + 1))
      attempt++
    }
  }
  throw lastErr
}

async function refreshSession(): Promise<void> {
  // Per-tab dedup: concurrent 401s from different requests share a single refresh.
  // Cross-tab dedup intentionally omitted - a stale localStorage timestamp would
  // incorrectly skip a real refresh after page reload (the bug behind the regression).
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = postRefreshWithRetry().then(
    () => {
      refreshInFlight = null
    },
    (err: unknown) => {
      refreshInFlight = null
      throw err
    },
  )
  return refreshInFlight
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriedRequest | undefined
    const status = error.response?.status

    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error)
    }

    if (original.url?.includes("/api/auth/")) {
      return Promise.reject(error)
    }

    original._retried = true
    try {
      await refreshSession()
      return api.request(original)
    } catch (refreshError) {
      return Promise.reject(refreshError)
    }
  },
)

export function isAuthError(err: unknown): boolean {
  const ax = err as AxiosError | undefined
  return ax?.response?.status === 401 || ax?.response?.status === 403
}
